import { useCallback, useMemo } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { CreditPackOfferDocument, type OfferedCreditPackFieldsFragment } from '@/graphql';

export const CREDIT_PACKS_PAGE_SIZE = 20;

/** The packs the payer may buy now, or why it may not; only a billing admin may ask. */
export function useCreditPackOffer({ skip = false }: { skip?: boolean } = {}) {
  const { data, loading, error, networkStatus, fetchMore, refetch } = useQuery(
    CreditPackOfferDocument,
    {
      variables: { first: CREDIT_PACKS_PAGE_SIZE },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip
    }
  );

  const offer = data?.creditPackOffer;
  const packs: OfferedCreditPackFieldsFragment[] = useMemo(
    () => (offer?.packs.edges ?? []).map(edge => edge.node),
    [offer]
  );
  const hasMore = offer?.packs.pageInfo.hasNextPage ?? false;
  const endCursor = offer?.packs.pageInfo.endCursor ?? null;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || endCursor === null) return;
    void fetchMore({ variables: { after: endCursor } });
  }, [endCursor, fetchMore, hasMore, loadingMore]);

  return {
    /** `undefined` until answered; `null` for a payer without an offer. */
    offer: data === undefined ? undefined : (offer ?? null),
    packs,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch
  };
}
